/**
 * InvitePanel — invite a person and say what they should be able to open, in one
 * step.
 *
 * WHY ONE STEP. Inviting and granting used to live in different places: approve
 * the request on one tab, then find the person in the roster and grant roles on
 * another. In the gap the invited person can sign in and land on an app where
 * every surface is closed (default-deny), which reads to them as a broken invite
 * and to the admin as "I did invite them". Choosing the pages at invite time means
 * their access is right the first time they arrive.
 *
 * Same page-picker vocabulary as PageAccessEditor, and the same honesty rule:
 * roles are coarser than pages, so if the resolved role opens more than was
 * ticked, this says so BEFORE the invite goes out.
 *
 * SENDING REAL MAIL. Submitting causes Microsoft to email a real person, so the
 * panel is collapsed by default, the button says exactly what it does, and a 207
 * (invited but the grant failed) is surfaced as a partial success — never as
 * plain success, because the email has already gone out and the admin would
 * otherwise believe access was set.
 */
import { type FormEvent, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@andavo/web-ui';
import { useInviteUser } from '../data-access/useAdmin';
import {
  MODULE_LABEL,
  PAGE_MODULES,
  pageByKey,
  pagesForModule,
  rolesForPages,
} from '../lib/page-registry';
import { AdminError, Panel } from './primitives';

function labelFor(pageKey: string): string {
  const page = pageByKey(pageKey);
  if (!page) return pageKey;
  return page.module === 'admin'
    ? page.label
    : `${MODULE_LABEL[page.module]} › ${page.label}`;
}

export function InvitePanel() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const invite = useInviteUser();

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const solution = useMemo(() => rolesForPages(selected), [selected]);
  const grantedSet = useMemo(
    () => new Set(solution.granted),
    [solution.granted],
  );

  const trimmed = email.trim().toLowerCase();
  const emailLooksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);

  const reset = () => {
    setEmail('');
    setSelected([]);
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!emailLooksValid) return;
    invite.mutate(
      { email: trimmed, roles: solution.roles },
      {
        onSuccess: (data) => {
          if (data.roles_error) {
            // The email HAS gone out. Say both halves.
            toast.error(
              `Invited ${data.email}, but access was NOT set — ${data.roles_error}`,
            );
          } else if (data.roles.length === 0) {
            toast.success(
              `Invited ${data.email} with no access yet — grant it from the roster.`,
            );
          } else {
            toast.success(
              `Invited ${data.email} with access to ${solution.granted.length} page${
                solution.granted.length === 1 ? '' : 's'
              }.`,
            );
          }
          reset();
          setOpen(false);
        },
        onError: (err) =>
          toast.error((err as Error)?.message ?? 'Invite failed'),
      },
    );
  };

  if (!open) {
    return (
      <div className="mb-4">
        <Button
          data-testid="invite-open"
          onClick={() => setOpen(true)}
          size="sm"
          type="button"
        >
          Invite someone
        </Button>
      </div>
    );
  }

  return (
    <Panel
      className="mb-4"
      title="Invite someone"
      actions={
        <Button
          data-testid="invite-cancel"
          onClick={() => {
            setOpen(false);
            reset();
          }}
          size="sm"
          type="button"
          variant="secondary"
        >
          Cancel
        </Button>
      }
    >
      <form data-testid="invite-form" onSubmit={onSubmit}>
        {invite.isError ? (
          <div className="mb-3">
            <AdminError
              message={`Invite failed: ${
                (invite.error as Error)?.message ?? 'unknown'
              }`}
            />
          </div>
        ) : null}

        <label className="mb-3 flex min-w-0 flex-col gap-1 font-inter text-content-secondary text-sm">
          Their work email
          <input
            className="w-full rounded-md border border-border-subtle bg-surface-background px-3 py-1.5 font-inter text-content-primary text-sm sm:w-96"
            data-testid="invite-email"
            onChange={(e) => setEmail(e.target.value)}
            placeholder="first.last@cbtravel.com"
            type="email"
            value={email}
          />
        </label>

        <p className="mb-2 font-inter text-content-secondary text-sm">
          What should they be able to open?
        </p>
        <div className="grid gap-3 md:grid-cols-2">
          {PAGE_MODULES.map((module) => (
            <fieldset
              className="rounded-md border border-border-subtle p-3"
              data-testid={`invite-module-${module}`}
              key={module}
            >
              <legend className="px-1 font-sans font-medium text-content-primary text-sm">
                {MODULE_LABEL[module]}
              </legend>
              <div className="flex flex-col gap-1.5">
                {pagesForModule(module).map((page) => {
                  const ticked = selectedSet.has(page.key);
                  const impliedOnly = !ticked && grantedSet.has(page.key);
                  return (
                    <label
                      className="flex cursor-pointer items-start gap-2 font-inter text-content-secondary text-sm"
                      key={page.key}
                    >
                      <input
                        checked={ticked || impliedOnly}
                        className="mt-0.5 size-4 shrink-0 rounded border-border-subtle accent-content-action"
                        data-implied={impliedOnly ? 'true' : undefined}
                        data-testid={`invite-page-${page.key}`}
                        disabled={invite.isPending}
                        onChange={() =>
                          setSelected((prev) =>
                            prev.includes(page.key)
                              ? prev.filter((k) => k !== page.key)
                              : [...prev, page.key],
                          )
                        }
                        type="checkbox"
                      />
                      <span className="text-content-primary">{page.label}</span>
                      {impliedOnly ? (
                        <span className="rounded-full bg-amber-500/10 px-1.5 py-0.5 font-medium text-amber-700 text-xs">
                          included by role
                        </span>
                      ) : null}
                    </label>
                  );
                })}
              </div>
            </fieldset>
          ))}
        </div>

        {solution.extra.length > 0 ? (
          <p
            className="mt-3 font-inter text-amber-700 text-xs"
            data-testid="invite-overgrant"
          >
            Also opens {solution.extra.length} page
            {solution.extra.length === 1 ? '' : 's'} you didn’t tick:{' '}
            {solution.extra.map(labelFor).join(' · ')}
          </p>
        ) : null}

        <div className="mt-4 flex flex-wrap items-center gap-3 border-border-subtle border-t pt-3">
          <Button
            data-testid="invite-submit"
            disabled={!emailLooksValid || invite.isPending}
            size="sm"
            type="submit"
          >
            {invite.isPending ? 'Sending…' : 'Send invite'}
          </Button>
          {/* Name the consequence at the point of action, not in a tooltip. */}
          <span className="font-inter text-content-tertiary text-xs">
            {emailLooksValid
              ? `Microsoft will email ${trimmed}. ${
                  solution.roles.length === 0
                    ? 'They will arrive with no access.'
                    : `Saves ${solution.roles.join(', ')}.`
                }`
              : 'Enter their email to continue.'}
          </span>
        </div>
      </form>
    </Panel>
  );
}

export default InvitePanel;
